import { logger } from '@heidi/runtime/logger';

import { VOICE_SAMPLE_RATE } from './constants';

/**
 * Queued playback of PCM16 mono 24 kHz audio chunks streamed from the backend.
 *
 * Chunks are scheduled back-to-back on a single AudioContext timeline so they
 * play gaplessly, at full volume for their whole life: playback either runs or
 * stops, with nothing in between. `flush()` stops everything immediately — used
 * once a barge-in is confirmed (or the BE sends a `clear_playback` frame).
 */
export class PcmPlayer {
  private audioContext: AudioContext | null = null;
  private nextStartTime = 0;
  private readonly sources = new Set<AudioBufferSourceNode>();
  private onPlayingChange?: (playing: boolean) => void;
  private playing = false;

  constructor(onPlayingChange?: (playing: boolean) => void) {
    this.onPlayingChange = onPlayingChange;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  private ensureContext(): AudioContext {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new AudioContext({ sampleRate: VOICE_SAMPLE_RATE });
      this.nextStartTime = 0;
    }
    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume();
    }
    return this.audioContext;
  }

  enqueue(pcm: ArrayBuffer): void {
    if (pcm.byteLength === 0) return;
    const ctx = this.ensureContext();

    const int16 = new Int16Array(pcm);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = (int16[i] ?? 0) / 0x8000;
    }

    const buffer = ctx.createBuffer(1, float32.length, VOICE_SAMPLE_RATE);
    buffer.copyToChannel(float32, 0);

    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now, this.nextStartTime);
    node.start(startAt);
    this.nextStartTime = startAt + buffer.duration;

    this.sources.add(node);
    this.setPlaying(true);
    node.onended = () => {
      this.sources.delete(node);
      if (this.sources.size === 0) {
        this.setPlaying(false);
      }
    };
  }

  /** Immediately stop and drop all queued/playing audio (barge-in). */
  flush(): void {
    for (const node of this.sources) {
      try {
        node.onended = null;
        node.stop();
        node.disconnect();
      } catch (error) {
        logger.debug('[voice] error stopping playback node', error);
      }
    }
    this.sources.clear();
    this.nextStartTime = this.audioContext?.currentTime ?? 0;
    this.setPlaying(false);
  }

  dispose(): void {
    this.flush();
    if (this.audioContext && this.audioContext.state !== 'closed') {
      void this.audioContext.close();
    }
    this.audioContext = null;
  }

  private setPlaying(playing: boolean): void {
    if (this.playing === playing) return;
    this.playing = playing;
    this.onPlayingChange?.(playing);
  }
}
